import {
  Cell,
  Slice,
  Address,
  Builder,
  beginCell,
  ComputeError,
  TupleItem,
  TupleReader,
  Dictionary,
  contractAddress,
  ContractProvider,
  Sender,
  Contract,
  ContractABI,
  ABIType,
  ABIGetter,
  ABIReceiver,
  TupleBuilder,
  DictionaryValue,
} from "@ton/core";

export type StateInit = {
  $$type: "StateInit";
  code: Cell;
  data: Cell;
};

export function storeStateInit(src: StateInit) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeRef(src.code);
    b_0.storeRef(src.data);
  };
}

export function loadStateInit(slice: Slice) {
  const sc_0 = slice;
  const _code = sc_0.loadRef();
  const _data = sc_0.loadRef();
  return { $$type: "StateInit" as const, code: _code, data: _data };
}

function loadTupleStateInit(source: TupleReader) {
  const _code = source.readCell();
  const _data = source.readCell();
  return { $$type: "StateInit" as const, code: _code, data: _data };
}

function storeTupleStateInit(source: StateInit) {
  const builder = new TupleBuilder();
  builder.writeCell(source.code);
  builder.writeCell(source.data);
  return builder.build();
}

function dictValueParserStateInit(): DictionaryValue<StateInit> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeStateInit(src)).endCell());
    },
    parse: (src) => {
      return loadStateInit(src.loadRef().beginParse());
    },
  };
}

export type Context = {
  $$type: "Context";
  bounced: boolean;
  sender: Address;
  value: bigint;
  raw: Cell;
};

export function storeContext(src: Context) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeBit(src.bounced);
    b_0.storeAddress(src.sender);
    b_0.storeInt(src.value, 257);
    b_0.storeRef(src.raw);
  };
}

export function loadContext(slice: Slice) {
  const sc_0 = slice;
  const _bounced = sc_0.loadBit();
  const _sender = sc_0.loadAddress();
  const _value = sc_0.loadIntBig(257);
  const _raw = sc_0.loadRef();
  return {
    $$type: "Context" as const,
    bounced: _bounced,
    sender: _sender,
    value: _value,
    raw: _raw,
  };
}

function loadTupleContext(source: TupleReader) {
  const _bounced = source.readBoolean();
  const _sender = source.readAddress();
  const _value = source.readBigNumber();
  const _raw = source.readCell();
  return {
    $$type: "Context" as const,
    bounced: _bounced,
    sender: _sender,
    value: _value,
    raw: _raw,
  };
}

function storeTupleContext(source: Context) {
  const builder = new TupleBuilder();
  builder.writeBoolean(source.bounced);
  builder.writeAddress(source.sender);
  builder.writeNumber(source.value);
  builder.writeSlice(source.raw);
  return builder.build();
}

function dictValueParserContext(): DictionaryValue<Context> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeContext(src)).endCell());
    },
    parse: (src) => {
      return loadContext(src.loadRef().beginParse());
    },
  };
}

export type SendParameters = {
  $$type: "SendParameters";
  bounce: boolean;
  to: Address;
  value: bigint;
  mode: bigint;
  body: Cell | null;
  code: Cell | null;
  data: Cell | null;
};

export function storeSendParameters(src: SendParameters) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeBit(src.bounce);
    b_0.storeAddress(src.to);
    b_0.storeInt(src.value, 257);
    b_0.storeInt(src.mode, 257);
    if (src.body !== null && src.body !== undefined) {
      b_0.storeBit(true).storeRef(src.body);
    } else {
      b_0.storeBit(false);
    }
    if (src.code !== null && src.code !== undefined) {
      b_0.storeBit(true).storeRef(src.code);
    } else {
      b_0.storeBit(false);
    }
    if (src.data !== null && src.data !== undefined) {
      b_0.storeBit(true).storeRef(src.data);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadSendParameters(slice: Slice) {
  const sc_0 = slice;
  const _bounce = sc_0.loadBit();
  const _to = sc_0.loadAddress();
  const _value = sc_0.loadIntBig(257);
  const _mode = sc_0.loadIntBig(257);
  const _body = sc_0.loadBit() ? sc_0.loadRef() : null;
  const _code = sc_0.loadBit() ? sc_0.loadRef() : null;
  const _data = sc_0.loadBit() ? sc_0.loadRef() : null;
  return {
    $$type: "SendParameters" as const,
    bounce: _bounce,
    to: _to,
    value: _value,
    mode: _mode,
    body: _body,
    code: _code,
    data: _data,
  };
}

function loadTupleSendParameters(source: TupleReader) {
  const _bounce = source.readBoolean();
  const _to = source.readAddress();
  const _value = source.readBigNumber();
  const _mode = source.readBigNumber();
  const _body = source.readCellOpt();
  const _code = source.readCellOpt();
  const _data = source.readCellOpt();
  return {
    $$type: "SendParameters" as const,
    bounce: _bounce,
    to: _to,
    value: _value,
    mode: _mode,
    body: _body,
    code: _code,
    data: _data,
  };
}

function storeTupleSendParameters(source: SendParameters) {
  const builder = new TupleBuilder();
  builder.writeBoolean(source.bounce);
  builder.writeAddress(source.to);
  builder.writeNumber(source.value);
  builder.writeNumber(source.mode);
  builder.writeCell(source.body);
  builder.writeCell(source.code);
  builder.writeCell(source.data);
  return builder.build();
}

function dictValueParserSendParameters(): DictionaryValue<SendParameters> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSendParameters(src)).endCell());
    },
    parse: (src) => {
      return loadSendParameters(src.loadRef().beginParse());
    },
  };
}

export type Deploy = {
  $$type: "Deploy";
  queryId: bigint;
};

export function storeDeploy(src: Deploy) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2490013878, 32);
    b_0.storeUint(src.queryId, 64);
  };
}

export function loadDeploy(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2490013878) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  return { $$type: "Deploy" as const, queryId: _queryId };
}

function loadTupleDeploy(source: TupleReader) {
  const _queryId = source.readBigNumber();
  return { $$type: "Deploy" as const, queryId: _queryId };
}

function storeTupleDeploy(source: Deploy) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  return builder.build();
}

function dictValueParserDeploy(): DictionaryValue<Deploy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeDeploy(src)).endCell());
    },
    parse: (src) => {
      return loadDeploy(src.loadRef().beginParse());
    },
  };
}

export type DeployOk = {
  $$type: "DeployOk";
  queryId: bigint;
};

export function storeDeployOk(src: DeployOk) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2952335191, 32);
    b_0.storeUint(src.queryId, 64);
  };
}

export function loadDeployOk(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2952335191) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  return { $$type: "DeployOk" as const, queryId: _queryId };
}

function loadTupleDeployOk(source: TupleReader) {
  const _queryId = source.readBigNumber();
  return { $$type: "DeployOk" as const, queryId: _queryId };
}

function storeTupleDeployOk(source: DeployOk) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  return builder.build();
}

function dictValueParserDeployOk(): DictionaryValue<DeployOk> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeDeployOk(src)).endCell());
    },
    parse: (src) => {
      return loadDeployOk(src.loadRef().beginParse());
    },
  };
}

export type FactoryDeploy = {
  $$type: "FactoryDeploy";
  queryId: bigint;
  cashback: Address;
};

export function storeFactoryDeploy(src: FactoryDeploy) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1829761339, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.cashback);
  };
}

export function loadFactoryDeploy(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1829761339) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  const _cashback = sc_0.loadAddress();
  return {
    $$type: "FactoryDeploy" as const,
    queryId: _queryId,
    cashback: _cashback,
  };
}

function loadTupleFactoryDeploy(source: TupleReader) {
  const _queryId = source.readBigNumber();
  const _cashback = source.readAddress();
  return {
    $$type: "FactoryDeploy" as const,
    queryId: _queryId,
    cashback: _cashback,
  };
}

function storeTupleFactoryDeploy(source: FactoryDeploy) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.cashback);
  return builder.build();
}

function dictValueParserFactoryDeploy(): DictionaryValue<FactoryDeploy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeFactoryDeploy(src)).endCell());
    },
    parse: (src) => {
      return loadFactoryDeploy(src.loadRef().beginParse());
    },
  };
}

export type ChangeOwner = {
  $$type: "ChangeOwner";
  queryId: bigint;
  newOwner: Address;
};

export function storeChangeOwner(src: ChangeOwner) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2174598809, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.newOwner);
  };
}

export function loadChangeOwner(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2174598809) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  const _newOwner = sc_0.loadAddress();
  return {
    $$type: "ChangeOwner" as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function loadTupleChangeOwner(source: TupleReader) {
  const _queryId = source.readBigNumber();
  const _newOwner = source.readAddress();
  return {
    $$type: "ChangeOwner" as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function storeTupleChangeOwner(source: ChangeOwner) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.newOwner);
  return builder.build();
}

function dictValueParserChangeOwner(): DictionaryValue<ChangeOwner> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeChangeOwner(src)).endCell());
    },
    parse: (src) => {
      return loadChangeOwner(src.loadRef().beginParse());
    },
  };
}

export type ChangeOwnerOk = {
  $$type: "ChangeOwnerOk";
  queryId: bigint;
  newOwner: Address;
};

export function storeChangeOwnerOk(src: ChangeOwnerOk) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(846932810, 32);
    b_0.storeUint(src.queryId, 64);
    b_0.storeAddress(src.newOwner);
  };
}

export function loadChangeOwnerOk(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 846932810) {
    throw Error("Invalid prefix");
  }
  const _queryId = sc_0.loadUintBig(64);
  const _newOwner = sc_0.loadAddress();
  return {
    $$type: "ChangeOwnerOk" as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function loadTupleChangeOwnerOk(source: TupleReader) {
  const _queryId = source.readBigNumber();
  const _newOwner = source.readAddress();
  return {
    $$type: "ChangeOwnerOk" as const,
    queryId: _queryId,
    newOwner: _newOwner,
  };
}

function storeTupleChangeOwnerOk(source: ChangeOwnerOk) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.queryId);
  builder.writeAddress(source.newOwner);
  return builder.build();
}

function dictValueParserChangeOwnerOk(): DictionaryValue<ChangeOwnerOk> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeChangeOwnerOk(src)).endCell());
    },
    parse: (src) => {
      return loadChangeOwnerOk(src.loadRef().beginParse());
    },
  };
}

export type NftTransfer = {
  $$type: "NftTransfer";
  query_id: bigint;
  new_owner: Address;
  response_destination: Address;
  custom_payload: Cell | null;
  forward_amount: bigint;
  forward_payload: Cell;
};

export function storeNftTransfer(src: NftTransfer) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1607220500, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.new_owner);
    b_0.storeAddress(src.response_destination);
    if (src.custom_payload !== null && src.custom_payload !== undefined) {
      b_0.storeBit(true).storeRef(src.custom_payload);
    } else {
      b_0.storeBit(false);
    }
    b_0.storeCoins(src.forward_amount);
    b_0.storeBuilder(src.forward_payload.asBuilder());
  };
}

export function loadNftTransfer(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1607220500) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _new_owner = sc_0.loadAddress();
  const _response_destination = sc_0.loadAddress();
  const _custom_payload = sc_0.loadBit() ? sc_0.loadRef() : null;
  const _forward_amount = sc_0.loadCoins();
  const _forward_payload = sc_0.asCell();
  return {
    $$type: "NftTransfer" as const,
    query_id: _query_id,
    new_owner: _new_owner,
    response_destination: _response_destination,
    custom_payload: _custom_payload,
    forward_amount: _forward_amount,
    forward_payload: _forward_payload,
  };
}

function loadTupleNftTransfer(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _new_owner = source.readAddress();
  const _response_destination = source.readAddress();
  const _custom_payload = source.readCellOpt();
  const _forward_amount = source.readBigNumber();
  const _forward_payload = source.readCell();
  return {
    $$type: "NftTransfer" as const,
    query_id: _query_id,
    new_owner: _new_owner,
    response_destination: _response_destination,
    custom_payload: _custom_payload,
    forward_amount: _forward_amount,
    forward_payload: _forward_payload,
  };
}

function storeTupleNftTransfer(source: NftTransfer) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.new_owner);
  builder.writeAddress(source.response_destination);
  builder.writeCell(source.custom_payload);
  builder.writeNumber(source.forward_amount);
  builder.writeSlice(source.forward_payload);
  return builder.build();
}

function dictValueParserNftTransfer(): DictionaryValue<NftTransfer> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeNftTransfer(src)).endCell());
    },
    parse: (src) => {
      return loadNftTransfer(src.loadRef().beginParse());
    },
  };
}

export type NftGetStaticData = {
  $$type: "NftGetStaticData";
  query_id: bigint;
};

export function storeNftGetStaticData(src: NftGetStaticData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(801842850, 32);
    b_0.storeUint(src.query_id, 64);
  };
}

export function loadNftGetStaticData(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 801842850) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  return { $$type: "NftGetStaticData" as const, query_id: _query_id };
}

function loadTupleNftGetStaticData(source: TupleReader) {
  const _query_id = source.readBigNumber();
  return { $$type: "NftGetStaticData" as const, query_id: _query_id };
}

function storeTupleNftGetStaticData(source: NftGetStaticData) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  return builder.build();
}

function dictValueParserNftGetStaticData(): DictionaryValue<NftGetStaticData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeNftGetStaticData(src)).endCell());
    },
    parse: (src) => {
      return loadNftGetStaticData(src.loadRef().beginParse());
    },
  };
}

export type NftReportStaticData = {
  $$type: "NftReportStaticData";
  query_id: bigint;
  index: bigint;
  collection: Address;
};

export function storeNftReportStaticData(src: NftReportStaticData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2339837749, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeInt(src.index, 257);
    b_0.storeAddress(src.collection);
  };
}

export function loadNftReportStaticData(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2339837749) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _index = sc_0.loadIntBig(257);
  const _collection = sc_0.loadAddress();
  return {
    $$type: "NftReportStaticData" as const,
    query_id: _query_id,
    index: _index,
    collection: _collection,
  };
}

function loadTupleNftReportStaticData(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _index = source.readBigNumber();
  const _collection = source.readAddress();
  return {
    $$type: "NftReportStaticData" as const,
    query_id: _query_id,
    index: _index,
    collection: _collection,
  };
}

function storeTupleNftReportStaticData(source: NftReportStaticData) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.index);
  builder.writeAddress(source.collection);
  return builder.build();
}

function dictValueParserNftReportStaticData(): DictionaryValue<NftReportStaticData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeNftReportStaticData(src)).endCell()
      );
    },
    parse: (src) => {
      return loadNftReportStaticData(src.loadRef().beginParse());
    },
  };
}

export type ProveOwnership = {
  $$type: "ProveOwnership";
  query_id: bigint;
  dest: Address;
  forward_payload: Cell;
  with_content: boolean;
};

export function storeProveOwnership(src: ProveOwnership) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(81711432, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.dest);
    b_0.storeRef(src.forward_payload);
    b_0.storeBit(src.with_content);
  };
}

export function loadProveOwnership(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 81711432) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _dest = sc_0.loadAddress();
  const _forward_payload = sc_0.loadRef();
  const _with_content = sc_0.loadBit();
  return {
    $$type: "ProveOwnership" as const,
    query_id: _query_id,
    dest: _dest,
    forward_payload: _forward_payload,
    with_content: _with_content,
  };
}

function loadTupleProveOwnership(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _dest = source.readAddress();
  const _forward_payload = source.readCell();
  const _with_content = source.readBoolean();
  return {
    $$type: "ProveOwnership" as const,
    query_id: _query_id,
    dest: _dest,
    forward_payload: _forward_payload,
    with_content: _with_content,
  };
}

function storeTupleProveOwnership(source: ProveOwnership) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.dest);
  builder.writeCell(source.forward_payload);
  builder.writeBoolean(source.with_content);
  return builder.build();
}

function dictValueParserProveOwnership(): DictionaryValue<ProveOwnership> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeProveOwnership(src)).endCell());
    },
    parse: (src) => {
      return loadProveOwnership(src.loadRef().beginParse());
    },
  };
}

export type OwnershipProof = {
  $$type: "OwnershipProof";
  query_id: bigint;
  item_id: bigint;
  owner: Address;
  data: Cell;
  revoked_at: bigint;
  content: Cell | null;
};

export function storeOwnershipProof(src: OwnershipProof) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(86296494, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeUint(src.item_id, 256);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.data);
    b_0.storeUint(src.revoked_at, 64);
    if (src.content !== null && src.content !== undefined) {
      b_0.storeBit(true).storeRef(src.content);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadOwnershipProof(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 86296494) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _item_id = sc_0.loadUintBig(256);
  const _owner = sc_0.loadAddress();
  const _data = sc_0.loadRef();
  const _revoked_at = sc_0.loadUintBig(64);
  const _content = sc_0.loadBit() ? sc_0.loadRef() : null;
  return {
    $$type: "OwnershipProof" as const,
    query_id: _query_id,
    item_id: _item_id,
    owner: _owner,
    data: _data,
    revoked_at: _revoked_at,
    content: _content,
  };
}

function loadTupleOwnershipProof(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _item_id = source.readBigNumber();
  const _owner = source.readAddress();
  const _data = source.readCell();
  const _revoked_at = source.readBigNumber();
  const _content = source.readCellOpt();
  return {
    $$type: "OwnershipProof" as const,
    query_id: _query_id,
    item_id: _item_id,
    owner: _owner,
    data: _data,
    revoked_at: _revoked_at,
    content: _content,
  };
}

function storeTupleOwnershipProof(source: OwnershipProof) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.item_id);
  builder.writeAddress(source.owner);
  builder.writeCell(source.data);
  builder.writeNumber(source.revoked_at);
  builder.writeCell(source.content);
  return builder.build();
}

function dictValueParserOwnershipProof(): DictionaryValue<OwnershipProof> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeOwnershipProof(src)).endCell());
    },
    parse: (src) => {
      return loadOwnershipProof(src.loadRef().beginParse());
    },
  };
}

export type RequestOwner = {
  $$type: "RequestOwner";
  query_id: bigint;
  dest: Address;
  forward_payload: Cell;
  with_content: boolean;
};

export function storeRequestOwner(src: RequestOwner) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(3502489578, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.dest);
    b_0.storeRef(src.forward_payload);
    b_0.storeBit(src.with_content);
  };
}

export function loadRequestOwner(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 3502489578) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _dest = sc_0.loadAddress();
  const _forward_payload = sc_0.loadRef();
  const _with_content = sc_0.loadBit();
  return {
    $$type: "RequestOwner" as const,
    query_id: _query_id,
    dest: _dest,
    forward_payload: _forward_payload,
    with_content: _with_content,
  };
}

function loadTupleRequestOwner(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _dest = source.readAddress();
  const _forward_payload = source.readCell();
  const _with_content = source.readBoolean();
  return {
    $$type: "RequestOwner" as const,
    query_id: _query_id,
    dest: _dest,
    forward_payload: _forward_payload,
    with_content: _with_content,
  };
}

function storeTupleRequestOwner(source: RequestOwner) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.dest);
  builder.writeCell(source.forward_payload);
  builder.writeBoolean(source.with_content);
  return builder.build();
}

function dictValueParserRequestOwner(): DictionaryValue<RequestOwner> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeRequestOwner(src)).endCell());
    },
    parse: (src) => {
      return loadRequestOwner(src.loadRef().beginParse());
    },
  };
}

export type OwnerInfo = {
  $$type: "OwnerInfo";
  query_id: bigint;
  item_id: bigint;
  initiator: Address;
  owner: Address;
  data: Cell;
  revoked_at: bigint;
  content: Cell | null;
};

export function storeOwnerInfo(src: OwnerInfo) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(232130531, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeUint(src.item_id, 256);
    b_0.storeAddress(src.initiator);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.data);
    b_0.storeUint(src.revoked_at, 64);
    if (src.content !== null && src.content !== undefined) {
      b_0.storeBit(true).storeRef(src.content);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadOwnerInfo(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 232130531) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _item_id = sc_0.loadUintBig(256);
  const _initiator = sc_0.loadAddress();
  const _owner = sc_0.loadAddress();
  const _data = sc_0.loadRef();
  const _revoked_at = sc_0.loadUintBig(64);
  const _content = sc_0.loadBit() ? sc_0.loadRef() : null;
  return {
    $$type: "OwnerInfo" as const,
    query_id: _query_id,
    item_id: _item_id,
    initiator: _initiator,
    owner: _owner,
    data: _data,
    revoked_at: _revoked_at,
    content: _content,
  };
}

function loadTupleOwnerInfo(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _item_id = source.readBigNumber();
  const _initiator = source.readAddress();
  const _owner = source.readAddress();
  const _data = source.readCell();
  const _revoked_at = source.readBigNumber();
  const _content = source.readCellOpt();
  return {
    $$type: "OwnerInfo" as const,
    query_id: _query_id,
    item_id: _item_id,
    initiator: _initiator,
    owner: _owner,
    data: _data,
    revoked_at: _revoked_at,
    content: _content,
  };
}

function storeTupleOwnerInfo(source: OwnerInfo) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.item_id);
  builder.writeAddress(source.initiator);
  builder.writeAddress(source.owner);
  builder.writeCell(source.data);
  builder.writeNumber(source.revoked_at);
  builder.writeCell(source.content);
  return builder.build();
}

function dictValueParserOwnerInfo(): DictionaryValue<OwnerInfo> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeOwnerInfo(src)).endCell());
    },
    parse: (src) => {
      return loadOwnerInfo(src.loadRef().beginParse());
    },
  };
}

export type SetValidator = {
  $$type: "SetValidator";
  pub_key: bigint;
};

export function storeSetValidator(src: SetValidator) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(632774787, 32);
    b_0.storeUint(src.pub_key, 256);
  };
}

export function loadSetValidator(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 632774787) {
    throw Error("Invalid prefix");
  }
  const _pub_key = sc_0.loadUintBig(256);
  return { $$type: "SetValidator" as const, pub_key: _pub_key };
}

function loadTupleSetValidator(source: TupleReader) {
  const _pub_key = source.readBigNumber();
  return { $$type: "SetValidator" as const, pub_key: _pub_key };
}

function storeTupleSetValidator(source: SetValidator) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.pub_key);
  return builder.build();
}

function dictValueParserSetValidator(): DictionaryValue<SetValidator> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetValidator(src)).endCell());
    },
    parse: (src) => {
      return loadSetValidator(src.loadRef().beginParse());
    },
  };
}

export type SetData = {
  $$type: "SetData";
  content: Cell | null;
  set_score_exec_fee: bigint | null;
  item_reserve_balance: bigint | null;
  collection_reserve_balance: bigint | null;
};

export function storeSetData(src: SetData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2189193868, 32);
    if (src.content !== null && src.content !== undefined) {
      b_0.storeBit(true).storeRef(src.content);
    } else {
      b_0.storeBit(false);
    }
    if (
      src.set_score_exec_fee !== null &&
      src.set_score_exec_fee !== undefined
    ) {
      b_0.storeBit(true).storeCoins(src.set_score_exec_fee);
    } else {
      b_0.storeBit(false);
    }
    if (
      src.item_reserve_balance !== null &&
      src.item_reserve_balance !== undefined
    ) {
      b_0.storeBit(true).storeCoins(src.item_reserve_balance);
    } else {
      b_0.storeBit(false);
    }
    if (
      src.collection_reserve_balance !== null &&
      src.collection_reserve_balance !== undefined
    ) {
      b_0.storeBit(true).storeCoins(src.collection_reserve_balance);
    } else {
      b_0.storeBit(false);
    }
  };
}

export function loadSetData(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2189193868) {
    throw Error("Invalid prefix");
  }
  const _content = sc_0.loadBit() ? sc_0.loadRef() : null;
  const _set_score_exec_fee = sc_0.loadBit() ? sc_0.loadCoins() : null;
  const _item_reserve_balance = sc_0.loadBit() ? sc_0.loadCoins() : null;
  const _collection_reserve_balance = sc_0.loadBit() ? sc_0.loadCoins() : null;
  return {
    $$type: "SetData" as const,
    content: _content,
    set_score_exec_fee: _set_score_exec_fee,
    item_reserve_balance: _item_reserve_balance,
    collection_reserve_balance: _collection_reserve_balance,
  };
}

function loadTupleSetData(source: TupleReader) {
  const _content = source.readCellOpt();
  const _set_score_exec_fee = source.readBigNumberOpt();
  const _item_reserve_balance = source.readBigNumberOpt();
  const _collection_reserve_balance = source.readBigNumberOpt();
  return {
    $$type: "SetData" as const,
    content: _content,
    set_score_exec_fee: _set_score_exec_fee,
    item_reserve_balance: _item_reserve_balance,
    collection_reserve_balance: _collection_reserve_balance,
  };
}

function storeTupleSetData(source: SetData) {
  const builder = new TupleBuilder();
  builder.writeCell(source.content);
  builder.writeNumber(source.set_score_exec_fee);
  builder.writeNumber(source.item_reserve_balance);
  builder.writeNumber(source.collection_reserve_balance);
  return builder.build();
}

function dictValueParserSetData(): DictionaryValue<SetData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetData(src)).endCell());
    },
    parse: (src) => {
      return loadSetData(src.loadRef().beginParse());
    },
  };
}

export type Signature = {
  $$type: "Signature";
  seqno: bigint;
  valid_until: bigint;
  signed_bytes: Buffer;
};

export function storeSignature(src: Signature) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(src.seqno, 32);
    b_0.storeUint(src.valid_until, 32);
    b_0.storeBuffer(src.signed_bytes);
  };
}

export function loadSignature(slice: Slice) {
  const sc_0 = slice;
  const _seqno = sc_0.loadUintBig(32);
  const _valid_until = sc_0.loadUintBig(32);
  const _signed_bytes = sc_0.loadBuffer(64);
  return {
    $$type: "Signature" as const,
    seqno: _seqno,
    valid_until: _valid_until,
    signed_bytes: _signed_bytes,
  };
}

function loadTupleSignature(source: TupleReader) {
  const _seqno = source.readBigNumber();
  const _valid_until = source.readBigNumber();
  const _signed_bytes = source.readBuffer();
  return {
    $$type: "Signature" as const,
    seqno: _seqno,
    valid_until: _valid_until,
    signed_bytes: _signed_bytes,
  };
}

function storeTupleSignature(source: Signature) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.seqno);
  builder.writeNumber(source.valid_until);
  builder.writeBuffer(source.signed_bytes);
  return builder.build();
}

function dictValueParserSignature(): DictionaryValue<Signature> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSignature(src)).endCell());
    },
    parse: (src) => {
      return loadSignature(src.loadRef().beginParse());
    },
  };
}

export type ScoreData = {
  $$type: "ScoreData";
  price: bigint;
  user: Address;
  content: Cell;
  score: bigint;
  referrer: Address | null;
  ref_amount: bigint;
};

export function storeScoreData(src: ScoreData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeCoins(src.price);
    b_0.storeAddress(src.user);
    b_0.storeRef(src.content);
    b_0.storeUint(src.score, 32);
    b_0.storeAddress(src.referrer);
    b_0.storeCoins(src.ref_amount);
  };
}

export function loadScoreData(slice: Slice) {
  const sc_0 = slice;
  const _price = sc_0.loadCoins();
  const _user = sc_0.loadAddress();
  const _content = sc_0.loadRef();
  const _score = sc_0.loadUintBig(32);
  const _referrer = sc_0.loadMaybeAddress();
  const _ref_amount = sc_0.loadCoins();
  return {
    $$type: "ScoreData" as const,
    price: _price,
    user: _user,
    content: _content,
    score: _score,
    referrer: _referrer,
    ref_amount: _ref_amount,
  };
}

function loadTupleScoreData(source: TupleReader) {
  const _price = source.readBigNumber();
  const _user = source.readAddress();
  const _content = source.readCell();
  const _score = source.readBigNumber();
  const _referrer = source.readAddressOpt();
  const _ref_amount = source.readBigNumber();
  return {
    $$type: "ScoreData" as const,
    price: _price,
    user: _user,
    content: _content,
    score: _score,
    referrer: _referrer,
    ref_amount: _ref_amount,
  };
}

function storeTupleScoreData(source: ScoreData) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.price);
  builder.writeAddress(source.user);
  builder.writeCell(source.content);
  builder.writeNumber(source.score);
  builder.writeAddress(source.referrer);
  builder.writeNumber(source.ref_amount);
  return builder.build();
}

function dictValueParserScoreData(): DictionaryValue<ScoreData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeScoreData(src)).endCell());
    },
    parse: (src) => {
      return loadScoreData(src.loadRef().beginParse());
    },
  };
}

export type SetScoreParent = {
  $$type: "SetScoreParent";
  signature: Signature;
  score_data: ScoreData;
};

export function storeSetScoreParent(src: SetScoreParent) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(3899072791, 32);
    b_0.store(storeSignature(src.signature));
    const b_1 = new Builder();
    b_1.store(storeScoreData(src.score_data));
    b_0.storeRef(b_1.endCell());
  };
}

export function loadSetScoreParent(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 3899072791) {
    throw Error("Invalid prefix");
  }
  const _signature = loadSignature(sc_0);
  const sc_1 = sc_0.loadRef().beginParse();
  const _score_data = loadScoreData(sc_1);
  return {
    $$type: "SetScoreParent" as const,
    signature: _signature,
    score_data: _score_data,
  };
}

function loadTupleSetScoreParent(source: TupleReader) {
  const _signature = loadTupleSignature(source.readTuple());
  const _score_data = loadTupleScoreData(source.readTuple());
  return {
    $$type: "SetScoreParent" as const,
    signature: _signature,
    score_data: _score_data,
  };
}

function storeTupleSetScoreParent(source: SetScoreParent) {
  const builder = new TupleBuilder();
  builder.writeTuple(storeTupleSignature(source.signature));
  builder.writeTuple(storeTupleScoreData(source.score_data));
  return builder.build();
}

function dictValueParserSetScoreParent(): DictionaryValue<SetScoreParent> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetScoreParent(src)).endCell());
    },
    parse: (src) => {
      return loadSetScoreParent(src.loadRef().beginParse());
    },
  };
}

export type SetScoreUser = {
  $$type: "SetScoreUser";
  score_data: ScoreData;
  item_index: bigint;
  reserve_balance: bigint;
};

export function storeSetScoreUser(src: SetScoreUser) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(549834415, 32);
    b_0.store(storeScoreData(src.score_data));
    b_0.storeUint(src.item_index, 64);
    const b_1 = new Builder();
    b_1.storeCoins(src.reserve_balance);
    b_0.storeRef(b_1.endCell());
  };
}

export function loadSetScoreUser(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 549834415) {
    throw Error("Invalid prefix");
  }
  const _score_data = loadScoreData(sc_0);
  const _item_index = sc_0.loadUintBig(64);
  const sc_1 = sc_0.loadRef().beginParse();
  const _reserve_balance = sc_1.loadCoins();
  return {
    $$type: "SetScoreUser" as const,
    score_data: _score_data,
    item_index: _item_index,
    reserve_balance: _reserve_balance,
  };
}

function loadTupleSetScoreUser(source: TupleReader) {
  const _score_data = loadTupleScoreData(source.readTuple());
  const _item_index = source.readBigNumber();
  const _reserve_balance = source.readBigNumber();
  return {
    $$type: "SetScoreUser" as const,
    score_data: _score_data,
    item_index: _item_index,
    reserve_balance: _reserve_balance,
  };
}

function storeTupleSetScoreUser(source: SetScoreUser) {
  const builder = new TupleBuilder();
  builder.writeTuple(storeTupleScoreData(source.score_data));
  builder.writeNumber(source.item_index);
  builder.writeNumber(source.reserve_balance);
  return builder.build();
}

function dictValueParserSetScoreUser(): DictionaryValue<SetScoreUser> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetScoreUser(src)).endCell());
    },
    parse: (src) => {
      return loadSetScoreUser(src.loadRef().beginParse());
    },
  };
}

export type SetScoreItem = {
  $$type: "SetScoreItem";
  data: ScoreData;
  reserve_balance: bigint;
};

export function storeSetScoreItem(src: SetScoreItem) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(2312732293, 32);
    b_0.store(storeScoreData(src.data));
    b_0.storeCoins(src.reserve_balance);
  };
}

export function loadSetScoreItem(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 2312732293) {
    throw Error("Invalid prefix");
  }
  const _data = loadScoreData(sc_0);
  const _reserve_balance = sc_0.loadCoins();
  return {
    $$type: "SetScoreItem" as const,
    data: _data,
    reserve_balance: _reserve_balance,
  };
}

function loadTupleSetScoreItem(source: TupleReader) {
  const _data = loadTupleScoreData(source.readTuple());
  const _reserve_balance = source.readBigNumber();
  return {
    $$type: "SetScoreItem" as const,
    data: _data,
    reserve_balance: _reserve_balance,
  };
}

function storeTupleSetScoreItem(source: SetScoreItem) {
  const builder = new TupleBuilder();
  builder.writeTuple(storeTupleScoreData(source.data));
  builder.writeNumber(source.reserve_balance);
  return builder.build();
}

function dictValueParserSetScoreItem(): DictionaryValue<SetScoreItem> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeSetScoreItem(src)).endCell());
    },
    parse: (src) => {
      return loadSetScoreItem(src.loadRef().beginParse());
    },
  };
}

export type ProxyDeployedRequest = {
  $$type: "ProxyDeployedRequest";
  seqno: bigint;
  score_data: ScoreData;
};

export function storeProxyDeployedRequest(src: ProxyDeployedRequest) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1044729494, 32);
    b_0.storeUint(src.seqno, 32);
    b_0.store(storeScoreData(src.score_data));
  };
}

export function loadProxyDeployedRequest(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1044729494) {
    throw Error("Invalid prefix");
  }
  const _seqno = sc_0.loadUintBig(32);
  const _score_data = loadScoreData(sc_0);
  return {
    $$type: "ProxyDeployedRequest" as const,
    seqno: _seqno,
    score_data: _score_data,
  };
}

function loadTupleProxyDeployedRequest(source: TupleReader) {
  const _seqno = source.readBigNumber();
  const _score_data = loadTupleScoreData(source.readTuple());
  return {
    $$type: "ProxyDeployedRequest" as const,
    seqno: _seqno,
    score_data: _score_data,
  };
}

function storeTupleProxyDeployedRequest(source: ProxyDeployedRequest) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.seqno);
  builder.writeTuple(storeTupleScoreData(source.score_data));
  return builder.build();
}

function dictValueParserProxyDeployedRequest(): DictionaryValue<ProxyDeployedRequest> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeProxyDeployedRequest(src)).endCell()
      );
    },
    parse: (src) => {
      return loadProxyDeployedRequest(src.loadRef().beginParse());
    },
  };
}

export type ProxyDeployedResponse = {
  $$type: "ProxyDeployedResponse";
  is_mint: boolean;
  score_data: ScoreData;
};

export function storeProxyDeployedResponse(src: ProxyDeployedResponse) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1897541925, 32);
    b_0.storeBit(src.is_mint);
    b_0.store(storeScoreData(src.score_data));
  };
}

export function loadProxyDeployedResponse(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1897541925) {
    throw Error("Invalid prefix");
  }
  const _is_mint = sc_0.loadBit();
  const _score_data = loadScoreData(sc_0);
  return {
    $$type: "ProxyDeployedResponse" as const,
    is_mint: _is_mint,
    score_data: _score_data,
  };
}

function loadTupleProxyDeployedResponse(source: TupleReader) {
  const _is_mint = source.readBoolean();
  const _score_data = loadTupleScoreData(source.readTuple());
  return {
    $$type: "ProxyDeployedResponse" as const,
    is_mint: _is_mint,
    score_data: _score_data,
  };
}

function storeTupleProxyDeployedResponse(source: ProxyDeployedResponse) {
  const builder = new TupleBuilder();
  builder.writeBoolean(source.is_mint);
  builder.writeTuple(storeTupleScoreData(source.score_data));
  return builder.build();
}

function dictValueParserProxyDeployedResponse(): DictionaryValue<ProxyDeployedResponse> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeProxyDeployedResponse(src)).endCell()
      );
    },
    parse: (src) => {
      return loadProxyDeployedResponse(src.loadRef().beginParse());
    },
  };
}

export type QueryScore = {
  $$type: "QueryScore";
  query_id: bigint;
  user: Address;
};

export function storeQueryScore(src: QueryScore) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(1789555454, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.user);
  };
}

export function loadQueryScore(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 1789555454) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _user = sc_0.loadAddress();
  return { $$type: "QueryScore" as const, query_id: _query_id, user: _user };
}

function loadTupleQueryScore(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _user = source.readAddress();
  return { $$type: "QueryScore" as const, query_id: _query_id, user: _user };
}

function storeTupleQueryScore(source: QueryScore) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.user);
  return builder.build();
}

function dictValueParserQueryScore(): DictionaryValue<QueryScore> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeQueryScore(src)).endCell());
    },
    parse: (src) => {
      return loadQueryScore(src.loadRef().beginParse());
    },
  };
}

export type QueryScoreProxy = {
  $$type: "QueryScoreProxy";
  query_id: bigint;
  destination: Address;
};

export function storeQueryScoreProxy(src: QueryScoreProxy) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(3893761339, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeAddress(src.destination);
  };
}

export function loadQueryScoreProxy(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 3893761339) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _destination = sc_0.loadAddress();
  return {
    $$type: "QueryScoreProxy" as const,
    query_id: _query_id,
    destination: _destination,
  };
}

function loadTupleQueryScoreProxy(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _destination = source.readAddress();
  return {
    $$type: "QueryScoreProxy" as const,
    query_id: _query_id,
    destination: _destination,
  };
}

function storeTupleQueryScoreProxy(source: QueryScoreProxy) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeAddress(source.destination);
  return builder.build();
}

function dictValueParserQueryScoreProxy(): DictionaryValue<QueryScoreProxy> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeQueryScoreProxy(src)).endCell());
    },
    parse: (src) => {
      return loadQueryScoreProxy(src.loadRef().beginParse());
    },
  };
}

export type QueryScoreProxyResponse = {
  $$type: "QueryScoreProxyResponse";
  query_id: bigint;
  score: bigint;
  user: Address;
  destination: Address;
};

export function storeQueryScoreProxyResponse(src: QueryScoreProxyResponse) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(3949093957, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeUint(src.score, 32);
    b_0.storeAddress(src.user);
    b_0.storeAddress(src.destination);
  };
}

export function loadQueryScoreProxyResponse(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 3949093957) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _score = sc_0.loadUintBig(32);
  const _user = sc_0.loadAddress();
  const _destination = sc_0.loadAddress();
  return {
    $$type: "QueryScoreProxyResponse" as const,
    query_id: _query_id,
    score: _score,
    user: _user,
    destination: _destination,
  };
}

function loadTupleQueryScoreProxyResponse(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _score = source.readBigNumber();
  const _user = source.readAddress();
  const _destination = source.readAddress();
  return {
    $$type: "QueryScoreProxyResponse" as const,
    query_id: _query_id,
    score: _score,
    user: _user,
    destination: _destination,
  };
}

function storeTupleQueryScoreProxyResponse(source: QueryScoreProxyResponse) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.score);
  builder.writeAddress(source.user);
  builder.writeAddress(source.destination);
  return builder.build();
}

function dictValueParserQueryScoreProxyResponse(): DictionaryValue<QueryScoreProxyResponse> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeQueryScoreProxyResponse(src)).endCell()
      );
    },
    parse: (src) => {
      return loadQueryScoreProxyResponse(src.loadRef().beginParse());
    },
  };
}

export type QueryScoreResponse = {
  $$type: "QueryScoreResponse";
  query_id: bigint;
  score: bigint;
  user: Address;
};

export function storeQueryScoreResponse(src: QueryScoreResponse) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeUint(356313908, 32);
    b_0.storeUint(src.query_id, 64);
    b_0.storeUint(src.score, 32);
    b_0.storeAddress(src.user);
  };
}

export function loadQueryScoreResponse(slice: Slice) {
  const sc_0 = slice;
  if (sc_0.loadUint(32) !== 356313908) {
    throw Error("Invalid prefix");
  }
  const _query_id = sc_0.loadUintBig(64);
  const _score = sc_0.loadUintBig(32);
  const _user = sc_0.loadAddress();
  return {
    $$type: "QueryScoreResponse" as const,
    query_id: _query_id,
    score: _score,
    user: _user,
  };
}

function loadTupleQueryScoreResponse(source: TupleReader) {
  const _query_id = source.readBigNumber();
  const _score = source.readBigNumber();
  const _user = source.readAddress();
  return {
    $$type: "QueryScoreResponse" as const,
    query_id: _query_id,
    score: _score,
    user: _user,
  };
}

function storeTupleQueryScoreResponse(source: QueryScoreResponse) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.query_id);
  builder.writeNumber(source.score);
  builder.writeAddress(source.user);
  return builder.build();
}

function dictValueParserQueryScoreResponse(): DictionaryValue<QueryScoreResponse> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(
        beginCell().store(storeQueryScoreResponse(src)).endCell()
      );
    },
    parse: (src) => {
      return loadQueryScoreResponse(src.loadRef().beginParse());
    },
  };
}

export type NftData = {
  $$type: "NftData";
  deployed: boolean;
  index: bigint;
  collection: Address;
  owner: Address;
  content: Cell;
};

export function storeNftData(src: NftData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeBit(src.deployed);
    b_0.storeInt(src.index, 257);
    b_0.storeAddress(src.collection);
    b_0.storeAddress(src.owner);
    b_0.storeRef(src.content);
  };
}

export function loadNftData(slice: Slice) {
  const sc_0 = slice;
  const _deployed = sc_0.loadBit();
  const _index = sc_0.loadIntBig(257);
  const _collection = sc_0.loadAddress();
  const _owner = sc_0.loadAddress();
  const _content = sc_0.loadRef();
  return {
    $$type: "NftData" as const,
    deployed: _deployed,
    index: _index,
    collection: _collection,
    owner: _owner,
    content: _content,
  };
}

function loadTupleNftData(source: TupleReader) {
  const _deployed = source.readBoolean();
  const _index = source.readBigNumber();
  const _collection = source.readAddress();
  const _owner = source.readAddress();
  const _content = source.readCell();
  return {
    $$type: "NftData" as const,
    deployed: _deployed,
    index: _index,
    collection: _collection,
    owner: _owner,
    content: _content,
  };
}

function storeTupleNftData(source: NftData) {
  const builder = new TupleBuilder();
  builder.writeBoolean(source.deployed);
  builder.writeNumber(source.index);
  builder.writeAddress(source.collection);
  builder.writeAddress(source.owner);
  builder.writeCell(source.content);
  return builder.build();
}

function dictValueParserNftData(): DictionaryValue<NftData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeNftData(src)).endCell());
    },
    parse: (src) => {
      return loadNftData(src.loadRef().beginParse());
    },
  };
}

export type CollectionData = {
  $$type: "CollectionData";
  next_index: bigint;
  content: Cell;
  owner: Address;
};

export function storeCollectionData(src: CollectionData) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeInt(src.next_index, 257);
    b_0.storeRef(src.content);
    b_0.storeAddress(src.owner);
  };
}

export function loadCollectionData(slice: Slice) {
  const sc_0 = slice;
  const _next_index = sc_0.loadIntBig(257);
  const _content = sc_0.loadRef();
  const _owner = sc_0.loadAddress();
  return {
    $$type: "CollectionData" as const,
    next_index: _next_index,
    content: _content,
    owner: _owner,
  };
}

function loadTupleCollectionData(source: TupleReader) {
  const _next_index = source.readBigNumber();
  const _content = source.readCell();
  const _owner = source.readAddress();
  return {
    $$type: "CollectionData" as const,
    next_index: _next_index,
    content: _content,
    owner: _owner,
  };
}

function storeTupleCollectionData(source: CollectionData) {
  const builder = new TupleBuilder();
  builder.writeNumber(source.next_index);
  builder.writeCell(source.content);
  builder.writeAddress(source.owner);
  return builder.build();
}

function dictValueParserCollectionData(): DictionaryValue<CollectionData> {
  return {
    serialize: (src, builder) => {
      builder.storeRef(beginCell().store(storeCollectionData(src)).endCell());
    },
    parse: (src) => {
      return loadCollectionData(src.loadRef().beginParse());
    },
  };
}

type UserScoreProxy_init_args = {
  $$type: "UserScoreProxy_init_args";
  parent: Address;
  user: Address;
};

function initUserScoreProxy_init_args(src: UserScoreProxy_init_args) {
  return (builder: Builder) => {
    const b_0 = builder;
    b_0.storeAddress(src.parent);
    b_0.storeAddress(src.user);
  };
}

async function UserScoreProxy_init(parent: Address, user: Address) {
  const __code = Cell.fromBase64(
    "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"
  );
  const __system = Cell.fromBase64(
    "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"
  );
  const builder = beginCell();
  builder.storeRef(__system);
  builder.storeUint(0, 1);
  initUserScoreProxy_init_args({
    $$type: "UserScoreProxy_init_args",
    parent,
    user,
  })(builder);
  const __data = builder.endCell();
  return { code: __code, data: __data };
}

const UserScoreProxy_errors: { [key: number]: { message: string } } = {
  2: { message: `Stack underflow` },
  3: { message: `Stack overflow` },
  4: { message: `Integer overflow` },
  5: { message: `Integer out of expected range` },
  6: { message: `Invalid opcode` },
  7: { message: `Type check error` },
  8: { message: `Cell overflow` },
  9: { message: `Cell underflow` },
  10: { message: `Dictionary error` },
  13: { message: `Out of gas error` },
  32: { message: `Method ID not found` },
  34: { message: `Action is invalid or not supported` },
  37: { message: `Not enough TON` },
  38: { message: `Not enough extra-currencies` },
  128: { message: `Null reference exception` },
  129: { message: `Invalid serialization prefix` },
  130: { message: `Invalid incoming message` },
  131: { message: `Constraints error` },
  132: { message: `Access denied` },
  133: { message: `Contract stopped` },
  134: { message: `Invalid argument` },
  135: { message: `Code of a contract was not found` },
  136: { message: `Invalid address` },
  137: { message: `Masterchain support is not enabled for this contract` },
  4429: { message: `Invalid sender` },
  5165: { message: `Not enough value` },
  17654: { message: `Invalid seqno` },
  17810: { message: `Invalid referrer addr/amount` },
  41207: { message: `invalid sender` },
  48401: { message: `Invalid signature` },
};

const UserScoreProxy_types: ABIType[] = [
  {
    name: "StateInit",
    header: null,
    fields: [
      { name: "code", type: { kind: "simple", type: "cell", optional: false } },
      { name: "data", type: { kind: "simple", type: "cell", optional: false } },
    ],
  },
  {
    name: "Context",
    header: null,
    fields: [
      {
        name: "bounced",
        type: { kind: "simple", type: "bool", optional: false },
      },
      {
        name: "sender",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "value",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      { name: "raw", type: { kind: "simple", type: "slice", optional: false } },
    ],
  },
  {
    name: "SendParameters",
    header: null,
    fields: [
      {
        name: "bounce",
        type: { kind: "simple", type: "bool", optional: false },
      },
      {
        name: "to",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "value",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "mode",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      { name: "body", type: { kind: "simple", type: "cell", optional: true } },
      { name: "code", type: { kind: "simple", type: "cell", optional: true } },
      { name: "data", type: { kind: "simple", type: "cell", optional: true } },
    ],
  },
  {
    name: "Deploy",
    header: 2490013878,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
    ],
  },
  {
    name: "DeployOk",
    header: 2952335191,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
    ],
  },
  {
    name: "FactoryDeploy",
    header: 1829761339,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "cashback",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "ChangeOwner",
    header: 2174598809,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "newOwner",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "ChangeOwnerOk",
    header: 846932810,
    fields: [
      {
        name: "queryId",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "newOwner",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "NftTransfer",
    header: 1607220500,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "new_owner",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "response_destination",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "custom_payload",
        type: { kind: "simple", type: "cell", optional: true },
      },
      {
        name: "forward_amount",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
      {
        name: "forward_payload",
        type: {
          kind: "simple",
          type: "slice",
          optional: false,
          format: "remainder",
        },
      },
    ],
  },
  {
    name: "NftGetStaticData",
    header: 801842850,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
    ],
  },
  {
    name: "NftReportStaticData",
    header: 2339837749,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "index",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "collection",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "ProveOwnership",
    header: 81711432,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "dest",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "forward_payload",
        type: { kind: "simple", type: "cell", optional: false },
      },
      {
        name: "with_content",
        type: { kind: "simple", type: "bool", optional: false },
      },
    ],
  },
  {
    name: "OwnershipProof",
    header: 86296494,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "item_id",
        type: { kind: "simple", type: "uint", optional: false, format: 256 },
      },
      {
        name: "owner",
        type: { kind: "simple", type: "address", optional: false },
      },
      { name: "data", type: { kind: "simple", type: "cell", optional: false } },
      {
        name: "revoked_at",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: true },
      },
    ],
  },
  {
    name: "RequestOwner",
    header: 3502489578,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "dest",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "forward_payload",
        type: { kind: "simple", type: "cell", optional: false },
      },
      {
        name: "with_content",
        type: { kind: "simple", type: "bool", optional: false },
      },
    ],
  },
  {
    name: "OwnerInfo",
    header: 232130531,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "item_id",
        type: { kind: "simple", type: "uint", optional: false, format: 256 },
      },
      {
        name: "initiator",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "owner",
        type: { kind: "simple", type: "address", optional: false },
      },
      { name: "data", type: { kind: "simple", type: "cell", optional: false } },
      {
        name: "revoked_at",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: true },
      },
    ],
  },
  {
    name: "SetValidator",
    header: 632774787,
    fields: [
      {
        name: "pub_key",
        type: { kind: "simple", type: "uint", optional: false, format: 256 },
      },
    ],
  },
  {
    name: "SetData",
    header: 2189193868,
    fields: [
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: true },
      },
      {
        name: "set_score_exec_fee",
        type: { kind: "simple", type: "uint", optional: true, format: "coins" },
      },
      {
        name: "item_reserve_balance",
        type: { kind: "simple", type: "uint", optional: true, format: "coins" },
      },
      {
        name: "collection_reserve_balance",
        type: { kind: "simple", type: "uint", optional: true, format: "coins" },
      },
    ],
  },
  {
    name: "Signature",
    header: null,
    fields: [
      {
        name: "seqno",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "valid_until",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "signed_bytes",
        type: {
          kind: "simple",
          type: "fixed-bytes",
          optional: false,
          format: 64,
        },
      },
    ],
  },
  {
    name: "ScoreData",
    header: null,
    fields: [
      {
        name: "price",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: false },
      },
      {
        name: "score",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "referrer",
        type: { kind: "simple", type: "address", optional: true },
      },
      {
        name: "ref_amount",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
    ],
  },
  {
    name: "SetScoreParent",
    header: 3899072791,
    fields: [
      {
        name: "signature",
        type: { kind: "simple", type: "Signature", optional: false },
      },
      {
        name: "score_data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
    ],
  },
  {
    name: "SetScoreUser",
    header: 549834415,
    fields: [
      {
        name: "score_data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
      {
        name: "item_index",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "reserve_balance",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
    ],
  },
  {
    name: "SetScoreItem",
    header: 2312732293,
    fields: [
      {
        name: "data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
      {
        name: "reserve_balance",
        type: {
          kind: "simple",
          type: "uint",
          optional: false,
          format: "coins",
        },
      },
    ],
  },
  {
    name: "ProxyDeployedRequest",
    header: 1044729494,
    fields: [
      {
        name: "seqno",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "score_data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
    ],
  },
  {
    name: "ProxyDeployedResponse",
    header: 1897541925,
    fields: [
      {
        name: "is_mint",
        type: { kind: "simple", type: "bool", optional: false },
      },
      {
        name: "score_data",
        type: { kind: "simple", type: "ScoreData", optional: false },
      },
    ],
  },
  {
    name: "QueryScore",
    header: 1789555454,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "QueryScoreProxy",
    header: 3893761339,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "destination",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "QueryScoreProxyResponse",
    header: 3949093957,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "score",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "destination",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "QueryScoreResponse",
    header: 356313908,
    fields: [
      {
        name: "query_id",
        type: { kind: "simple", type: "uint", optional: false, format: 64 },
      },
      {
        name: "score",
        type: { kind: "simple", type: "uint", optional: false, format: 32 },
      },
      {
        name: "user",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
  {
    name: "NftData",
    header: null,
    fields: [
      {
        name: "deployed",
        type: { kind: "simple", type: "bool", optional: false },
      },
      {
        name: "index",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "collection",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "owner",
        type: { kind: "simple", type: "address", optional: false },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: false },
      },
    ],
  },
  {
    name: "CollectionData",
    header: null,
    fields: [
      {
        name: "next_index",
        type: { kind: "simple", type: "int", optional: false, format: 257 },
      },
      {
        name: "content",
        type: { kind: "simple", type: "cell", optional: false },
      },
      {
        name: "owner",
        type: { kind: "simple", type: "address", optional: false },
      },
    ],
  },
];

const UserScoreProxy_getters: ABIGetter[] = [
  {
    name: "score_item_index",
    arguments: [],
    returnType: { kind: "simple", type: "int", optional: false, format: 257 },
  },
  {
    name: "seqno",
    arguments: [],
    returnType: { kind: "simple", type: "int", optional: false, format: 257 },
  },
];

const UserScoreProxy_receivers: ABIReceiver[] = [
  {
    receiver: "internal",
    message: { kind: "typed", type: "ProxyDeployedRequest" },
  },
  { receiver: "internal", message: { kind: "typed", type: "SetScoreUser" } },
  { receiver: "internal", message: { kind: "typed", type: "QueryScoreProxy" } },
];

export class UserScoreProxy implements Contract {
  static async init(parent: Address, user: Address) {
    return await UserScoreProxy_init(parent, user);
  }

  static async fromInit(parent: Address, user: Address) {
    const init = await UserScoreProxy_init(parent, user);
    const address = contractAddress(0, init);
    return new UserScoreProxy(address, init);
  }

  static fromAddress(address: Address) {
    return new UserScoreProxy(address);
  }

  readonly address: Address;
  readonly init?: { code: Cell; data: Cell };
  readonly abi: ContractABI = {
    types: UserScoreProxy_types,
    getters: UserScoreProxy_getters,
    receivers: UserScoreProxy_receivers,
    errors: UserScoreProxy_errors,
  };

  public constructor(address: Address, init?: { code: Cell; data: Cell }) {
    this.address = address;
    this.init = init;
  }

  async send(
    provider: ContractProvider,
    via: Sender,
    args: { value: bigint; bounce?: boolean | null | undefined },
    message: ProxyDeployedRequest | SetScoreUser | QueryScoreProxy
  ) {
    let body: Cell | null = null;
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "ProxyDeployedRequest"
    ) {
      body = beginCell().store(storeProxyDeployedRequest(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "SetScoreUser"
    ) {
      body = beginCell().store(storeSetScoreUser(message)).endCell();
    }
    if (
      message &&
      typeof message === "object" &&
      !(message instanceof Slice) &&
      message.$$type === "QueryScoreProxy"
    ) {
      body = beginCell().store(storeQueryScoreProxy(message)).endCell();
    }
    if (body === null) {
      throw new Error("Invalid message type");
    }

    await provider.internal(via, { ...args, body: body });
  }

  async getScoreItemIndex(provider: ContractProvider) {
    const builder = new TupleBuilder();
    const source = (await provider.get("score_item_index", builder.build()))
      .stack;
    const result = source.readBigNumber();
    return result;
  }

  async getSeqno(provider: ContractProvider) {
    const builder = new TupleBuilder();
    const source = (await provider.get("seqno", builder.build())).stack;
    const result = source.readBigNumber();
    return result;
  }
}
